namespace $ {

	type $shm_hitalama_mcp_client_request = {
		type: 'request'
		id: string
		method: string
		params?: Record< string, any >
		target?: {
			client_id?: string
			map_id?: string
			session_id?: string
		}
	}

	const $shm_hitalama_mcp_client_timeout = Symbol( 'hitalama_mcp_client_timeout' )

	export class $shm_hitalama_mcp_client extends $mol_object {

		private socket: WebSocket | null = null
		private socket_url = ''
		private reconnecting = false
		private registering = false

		app() {
			return null as any as $.$$.$shm_hitalama_app
		}

		url() {
			const url = this.$.$shm_hitalama_settings.agent_mcp_url()
			const token = this.$.$shm_hitalama_settings.agent_mcp_token()
			if( !url || !token ) return url

			const parsed = new URL( url, this.$.$mol_dom_context.document?.location.href )
			parsed.searchParams.set( 'token', token )
			return parsed.toString()
		}

		client_id( next?: string ) {
			const key = '$' + 'hitalama_mcp_client.client_id'
			const state = this.$.$mol_state_session
			const stored = state.value< string >( key, next )
			if( stored ) return stored
			const id = `tab_${ Date.now().toString( 36 ) }_${ Math.random().toString( 36 ).slice( 2 ) }`
			state.value( key, id )
			return id
		}

		auto() {
			this.connect()
			this.register_later()
			return null
		}

		connect() {
			if( typeof WebSocket === 'undefined' ) return
			const url = this.url()
			if( this.socket && this.socket_url !== url ) {
				this.socket.onopen = null
				this.socket.onmessage = null
				this.socket.onclose = null
				this.socket.onerror = null
				this.socket.close()
				this.socket = null
			}
			if( !url ) {
				this.socket_url = ''
				return
			}
			if( this.socket?.readyState === WebSocket.OPEN ) return
			if( this.socket?.readyState === WebSocket.CONNECTING ) return

			const socket = this.socket = new WebSocket( url )
			this.socket_url = url
			socket.onopen = ()=> {
				$mol_wire_async( this ).register().catch( $mol_fail_log )
			}
			socket.onmessage = event => $mol_wire_async( this ).message( event )
			socket.onclose = ()=> this.reconnect_later()
			socket.onerror = ()=> socket.close()
		}

		reconnect_later() {
			this.socket = null
			if( this.reconnecting ) return
			this.reconnecting = true
			new this.$.$mol_after_timeout( 1500, ()=> {
				this.reconnecting = false
				this.connect()
			} )
		}

		register_later() {
			if( this.registering ) return
			this.registering = true
			new this.$.$mol_after_timeout( 2000, ()=> {
				// Cold CRDT reads may suspend. Keep one heartbeat in flight until
				// it settles instead of throwing its Promise out of the timer.
				$mol_wire_async( this ).register().catch( $mol_fail_log ).finally( ()=> {
					this.registering = false
					this.register_later()
				} )
			} )
		}

		send( message: Record< string, any > ) {
			const socket = this.socket
			if( !socket || socket.readyState !== WebSocket.OPEN ) return
			socket.send( JSON.stringify( message ) )
		}

		register() {
			// Do not unpack the selected map just to discard the message offline.
			if( this.socket?.readyState !== WebSocket.OPEN ) return
			this.send({
				type: 'register',
				... this.client_info(),
			})
		}

		client_info() {
			const app = this.app()
			const map = this.map()
			const selected = this.selected_links()
			return {
				client_id: this.client_id(),
				application: this.$.$mol_state_arg.value( 'section' ) === 'applications' ? this.$.$mol_state_arg.value( 'component' ) ?? '' : '',
				map_id: map?.link().str ?? '',
				map_title: map?.title?.() ?? '',
				url: this.$.$mol_dom_context.document?.location.href ?? '',
				focused: this.$.$mol_dom_context.document?.hasFocus?.() ?? false,
				visible: this.$.$mol_dom_context.document?.visibilityState !== 'hidden',
				selected_count: selected.length,
				spread: app.spread?.() ?? '',
			}
		}

		map() {
			const id = this.app().map_id?.() ?? ''
			if( !id ) return null
			return this.$.$giper_baza_glob.Pawn( new this.$.$giper_baza_link( id ), this.$.$shm_hitalama_map )
		}

		map_page() {
			const id = this.app().map_id?.() ?? ''
			if( !id ) return null
			try {
				return this.app().Maps().Book( id ).Map_page() as $.$$.$shm_hitalama_map_page
			} catch( error ) {
				return null
			}
		}

		selected_links() {
			return this.map_page()?.selected_links?.() ?? []
		}

		message( event: MessageEvent ) {
			const request = JSON.parse( String( event.data ) ) as $shm_hitalama_mcp_client_request
			if( request.type !== 'request' ) return
			this.answer( request )
		}

		async answer( request: $shm_hitalama_mcp_client_request ) {
			try {
				const result = await this.$.$mol_wire_async( this ).handle_request( request )
				// Wire uses Error as a failure value. An async rejection can reach
				// this boundary as that value rather than reject the outer Promise.
				if( result instanceof Error ) throw result
				this.send({
					type: 'response',
					id: request.id,
					result,
				})
			} catch( error ) {
				this.send({
					type: 'response',
					id: request.id,
					error: {
						message: error instanceof Error ? error.message : String( error ),
					},
				})
			}
		}

		handle_request( request: $shm_hitalama_mcp_client_request ) {
			// Recheck on every wire replay, not just before the first async read.
			this.request_target_check( request )
			return this.handle( request.method, request.params ?? {} )
		}

		request_target_check( request: $shm_hitalama_mcp_client_request ) {
			const target = request.target
			if( !target ) return

			const client_id = target.client_id ?? ''
			if( client_id && client_id !== this.client_id() ) {
				throw new Error( `Hitalama MCP request is bound to client "${ client_id }", but this browser client is "${ this.client_id() }"` )
			}

			const map_id = target.map_id ?? ''
			if( !map_id ) return

			const current = this.app().map_id?.() ?? ''
			if( current === map_id ) return

			throw new Error( `Hitalama MCP request is bound to map "${ map_id }", but this browser client has map "${ current || 'none' }" open` )
		}

		private application_service?: $shm_hitalama_components_mcp
		private skills_service?: $shm_hitalama_skills_mcp
		application_modules() { return this.application_service ??= new this.$.$shm_hitalama_components_mcp }

		handle( method: string, params: Record< string, any > ) {
			if( method === 'skill_create' || method === 'skill_write' ) return $mol_wire_sync( this.skills_service ??= new this.$.$shm_hitalama_skills_mcp ).handle( method, params )
			if( method.startsWith( 'application_' ) ) return this.application_modules().handle( method, params )
			if( method === 'map_context' ) return this.map_context()
			if( method === 'map_store_list' ) return this.map_store_list()
			if( method === 'map_history' ) return this.map_history( params )
			if( method === 'selected_widgets' ) return this.selected_widgets()
			if( method === 'widgets' ) return this.widgets()
			if( method === 'widget_list' ) return this.widgets()
			if( method === 'widget_get' ) return this.widget_info( params.id, true )
			if( method === 'widget_text' ) return this.widget_text( params.id )
			if( method === 'widget_create' ) return this.widget_create( params )
			if( method === 'widgets_batch' ) return this.widgets_batch( params.items )
			if( method === 'group_create' ) return this.group_create( params )
			if( method === 'group_fit' ) return this.group_fit( params.id )
			if( method === 'group_ungroup' ) return this.group_ungroup( params.id )
			if( method === 'widget_update' ) return this.widget_update( params.id, params )
			if( method === 'widget_move_resize' ) return this.widget_move_resize( params.id, params )
			if( method === 'widget_open_view' ) return this.widget_open_view( params.id, params )
			if( method === 'widget_result' ) return this.widget_result( params.id, params )
			if( method === 'widget_delete' ) return this.widget_delete( params.id )
			if( method === 'widget_cut' ) return this.widget_delete( params.id )
			if( method === 'map_store_get' ) return this.map_store_get( params.key )
			if( method === 'map_store_set' ) return this.map_store_set( params.key, params.value )
			if( method === 'execute_js' ) return this.execute_js( params.code )
			throw new Error( `Unknown Hitalama bridge method "${ method }"` )
		}

		map_context() {
			const map = this.map()
			if( !map ) throw new Error( 'No Hitalama map is open in this browser client' )
			return {
				... this.client_info(),
				widgets_count: map.Widgets_list()?.remote_list().length ?? 0,
				selected_widgets: this.selected_widgets( false ),
				slideshow_enabled: map.slideshow_enabled(),
				slideshow_frame: map.slideshow_frame(),
				camera_locked: map.camera_locked(),
				widget_types: Object.keys( this.$.$shm_hitalama_map_widget_def_all ),
			}
		}

		map_store_list() {
			const map = this.map()
			if( !map ) throw new Error( 'No Hitalama map is open in this browser client' )
			return Object.keys( map.Store_json()?.val() ?? {} ).sort()
		}

		map_history( params: Record<string,any> ) {
			const map = this.map()
			if( !map ) throw new Error( 'No Hitalama map is open in this browser client' )
			const limit = params.limit ?? 20
			const offset = params.offset ?? 0
			if( !Number.isInteger(limit) || limit < 1 || limit > 100 ) throw new Error('History limit must be an integer from 1 to 100')
			if( !Number.isSafeInteger(offset) || offset < 0 || offset > 10000 ) throw new Error('History offset must be an integer from 0 to 10000')
			if( params.target !== undefined && (typeof params.target !== 'string' || !params.target) ) throw new Error('History target must be a non-empty string')
			// Read compact index entries, never full undo payloads or a new history root.
			const page = map.history_maybe()?.entries_page_for([ params.target ?? '__all__' ],limit,offset)
			return { count: page?.count ?? 0, limit, offset, events: page?.events.map(event=> ({
				id: event.link().str,
				time: event.time(),
				author: event.author(),
				title: event.data().title,
				change_types: [ ...new Set(event.data().changes.map(change=> change.type)) ],
			})) ?? [] }
		}

		widgets() {
			return this.map()?.Widgets_list()?.remote_list().map( ( widget: $shm_hitalama_map_widget ) => this.widget_info_from( widget, false ) ) ?? []
		}

		selected_widgets( text = true ) {
			const map = this.map()
			return this.selected_links().map( link => {
				const widget = map?.widget_by_link( link )
				return widget ? this.widget_info_from( widget, text ) : { id: link.str }
			} )
		}

		widget_by_id( id: string ) {
			if( !id ) throw new Error( 'Widget id is required' )
			const widgets = this.map()?.Widgets_list()?.remote_list() ?? []
			return widgets.find( ( widget: $shm_hitalama_map_widget ) => widget.link().str === id )
				?? widgets.find( ( widget: $shm_hitalama_map_widget ) => widget.title() === id )
				?? widgets.find( ( widget: $shm_hitalama_map_widget ) => widget.title().toLowerCase() === id.toLowerCase() )
		}

		widget_text( id: string ) {
			const widget = this.widget_by_id( id )
			if( !widget ) throw new Error( `Widget "${ id }" not found` )
			return widget.text()
		}

		widget_info( id: string, text: boolean ) {
			const widget = this.widget_by_id( id )
			if( !widget ) throw new Error( `Widget "${ id }" not found` )
			return this.widget_info_from( widget, text )
		}

		widget_info_from( widget: $shm_hitalama_map_widget, text: boolean ) {
			return {
				id: widget.link().str,
				title: widget.title(),
				type: widget.type(),
				x: widget.body_x(),
				y: widget.body_y(),
				width: widget.right_edge_x(),
				height: widget.bottom_edge_y(),
				opacity: widget.opacity(),
				selected: this.selected_links().some( link => link.str === widget.link().str ),
				text: text ? widget.text() : undefined,
				...(text ? {
					z_order: widget.z(),
					container_id: widget.container_widget()?.link_str() ?? null,
					children: widget.widgets().map(child=> child.link_str()),
					locked: widget.locked(),
					background_color: widget.background_color(),
					border_color: widget.border_color(),
					border_width: widget.border_width(),
					border_style: widget.border_style(),
					border_opacity: widget.border_opacity(),
					ratio: widget.type() === 'viewport_frame' ? widget.viewport_frame().ratio() : undefined,
				} : {}),
			}
		}

		/** Validate the complete plan once, before any item changes the map. */
		group_create_plan( ids: any ) {
			if(!Array.isArray(ids) || ids.length < 2 || ids.length > 100 || ids.some(id=>typeof id!=='string'||!id)) throw new Error('Group requires 2–100 widget IDs or titles')
			const page=this.map_page()
			if(!page) throw new Error('No Hitalama map page is open')
			const links=ids.map(id=>{
				const widget=this.widget_by_id(id)
				if(!widget) throw new Error(`Widget "${id}" not found`)
				return widget.link()
			})
			const roots=page.grouping_links(links)
			if(roots.length<2) throw new Error('Group requires at least two distinct roots; descendants of selected parents are already included')
			return {page,links:roots}
		}

		group_create( params: Record<string,any> ) {
			if(params.title!==undefined && (typeof params.title!=='string'||!params.title.trim())) throw new Error('Group title must be a non-empty string')
			const plan=this.$.$mol_wire_sync(this).group_create_plan(params.ids)
			const group=this.$.$mol_wire_sync(plan.page).grouping_widgets(plan.links)
			if(!group) throw new Error('Group was not created')
			if(params.title!==undefined) group.title(params.title)
			return this.widget_info_from(group,true)
		}

		group_existing_plan( id: string ) {
			const group=this.widget_by_id(id)
			if(!group || group.type()!=='group') throw new Error(`Widget "${id}" is not a group`)
			const page=this.map_page()
			if(!page) throw new Error('No Hitalama map page is open')
			return {page,group,children:this.map()!.widget_children_linked(group)}
		}

		group_fit( id: string ) {
			const {page,group}=this.$.$mol_wire_sync(this).group_existing_plan(id)
			const view=page.Widget(group.link()).Sub() as $.$$.$shm_hitalama_map_widget_float_group
			this.$.$mol_wire_sync(view).fit_to_widgets()
			return this.widget_info_from(group,true)
		}

		group_ungroup( id: string ) {
			const plan=this.$.$mol_wire_sync(this).group_existing_plan(id)
			this.$.$mol_wire_sync(plan.page).ungrouping_one(plan.group)
			return {removed_group_id:plan.group.link_str(),widgets:plan.children.map(widget=>this.widget_info_from(widget,true))}
		}

		/** Validate the complete plan once, before any item changes the map. */
		widgets_batch_plan( items: any ) {
			if(!Array.isArray(items) || items.length < 1 || items.length > 50) throw new Error('Batch must contain between 1 and 50 items')
			return items.map((item,index)=>{
				if(!item || !['create','update'].includes(item.action) || !item.params || typeof item.params !== 'object' || Array.isArray(item.params)) throw new Error(`Invalid batch item ${index}`)
				const params=item.params as Record<string,any>
				if(item.action === 'create') {
					if(typeof params.title !== 'string' || !params.title.trim()) throw new Error(`Batch create item ${index} requires a non-empty title`)
					const type=String(params.type || 'text')
					this.widget_type_check(type)
					this.widget_params_check(type,params)
					return {action:'create',params}
				}
				if(typeof params.id !== 'string' || !params.id) throw new Error(`Batch update item ${index} requires an id`)
				const widget=this.widget_by_id(params.id)
				if(!widget) throw new Error(`Widget "${params.id}" not found at batch item ${index}`)
				const type=String(params.type ?? widget.type())
				this.widget_type_check(type)
				this.widget_params_check(type,params)
				// Resolve titles before updates can rename other items.
				return {action:'update',params:{...params,id:widget.link_str()}}
			})
		}

		widgets_batch_item( item: {action:string,params:Record<string,any>} ) {
			return item.action === 'create' ? this.widget_create(item.params) : this.widget_update(item.params.id,item.params)
		}

		// Experimental preparation is deliberately disconnected from ordinary batches.
		// Advisory hints only, not retained CRDT data or permission/readiness proofs.
		private widgets_batch_prepared_histories = new Set<string>()

		widgets_batch_index_preparation() {
			const map = this.map()
			if( !map || !map.can_change() || !this.$.$giper_baza_yard.masters().length ) return null
			const history = map.history()
			if( this.widgets_batch_prepared_histories.has( history.link().str ) ) return null
			return this.$.$shm_hitalama_map_history_preparation.make({ history: $mol_const( history ) })
		}

		@ $mol_action
		widgets_batch_prepare_indexes() {
			// Keep this host stable through suspensions without a map-wide keepalive.
			const preparation = this.$.$mol_wire_sync( this ).widgets_batch_index_preparation()
			if( !preparation ) return
			preparation.prepare()
			const hints = this.widgets_batch_prepared_histories
			hints.add( preparation.history().link().str )
			if( hints.size > 16 ) hints.delete( hints.values().next().value! )
		}

		widgets_batch( items: any ) {
			const plan=this.$.$mol_wire_sync(this).widgets_batch_plan(items)
			const completed: unknown[]=[]
			for(let index=0;index<plan.length;++index) {
				try {
					// Each item has stable identity across a suspended batch replay.
					const result=this.$.$mol_wire_sync(this).widgets_batch_item(plan[index])
					if(result instanceof Error) throw result
					completed.push(result)
				} catch(error) {
					if($mol_promise_like(error)) throw error
					return {status:'failed',completed,failed_index:index,failed_item_may_have_changed:true,error:{message:this.$.$mol_error_message(error)}}
				}
			}
			return {status:'done',completed}
		}

		widget_create( params: Record< string, any > ) {
			const map = this.map()
			if( !map ) throw new Error( 'No Hitalama map is open in this browser client' )

			const type = String( params.type || 'text' )
			this.widget_type_check( type )
			this.widget_params_check( type, params )
			const x = Number( params.x ?? 0 )
			const y = Number( params.y ?? 0 )
			const width = Number( params.width ?? 320 )
			const height = Number( params.height ?? 180 )
			const title = params.title == null ? undefined : String( params.title )
			const text = params.text == null ? '' : String( params.text )
			const existing = this.$.$mol_wire_sync( this ).widget_create_existing( title )
			if( existing ) return { ...this.widget_info_from( existing, true ), created: false, existing_id: existing.link_str() }
			const widget = map.widget_add( type as any, [ x, y ], width, height, title, text )
			if( !widget ) throw new Error( `Widget "${ type }" was not created` )
			if( params.data !== undefined ) widget.data( params.data )
			if( params.enabled !== undefined ) widget.enabled( Boolean( params.enabled ) )
			this.widget_style_apply( widget, params )

			this.register()
			return { ...this.widget_info_from( widget, true ), created: true }
		}

		widget_create_existing( title?: string ) {
			if( !title ) return null
			return this.map()?.widget_by_title( title ) ?? null
		}

		widget_type_check( type: string ) {
			if( !Object.prototype.hasOwnProperty.call( this.$.$shm_hitalama_map_widget_def_all, type ) ) {
				throw new Error( `Unsupported widget type "${ type }". Read map_context.widget_types for available types.` )
			}
		}

		widget_params_check( type: string, params: Record<string,any> ) {
			if( params.z_order !== undefined && (!Number.isSafeInteger(params.z_order) || params.z_order < 0) ) throw new Error('z_order must be a non-negative safe integer')
			for( const key of [ 'x', 'y', 'width', 'height', 'opacity', 'border_width', 'border_opacity', 'ratio' ] ) {
				if( params[key] === undefined ) continue
				if( typeof params[key] !== 'number' || !Number.isFinite(params[key]) ) throw new Error(`Invalid ${key}: expected a finite number`)
				if( ['width','height','border_width','ratio'].includes(key) && params[key] < 0 ) throw new Error(`Invalid ${key}: must be non-negative`)
				if( ['opacity','border_opacity'].includes(key) && (params[key] < 0 || params[key] > 1) ) throw new Error(`Invalid ${key}: must be between 0 and 1`)
			}
			if( params.ratio !== undefined && type !== 'viewport_frame' ) throw new Error('ratio is supported only by viewport_frame widgets')
			if( params.locked !== undefined && typeof params.locked !== 'boolean' ) throw new Error('locked must be a boolean')
			for( const key of ['background_color','border_color','border_style'] ) {
				if( params[key] !== undefined && typeof params[key] !== 'string' ) throw new Error(`${key} must be a string`)
			}
		}

		widget_style_apply( widget: $shm_hitalama_map_widget, params: Record<string,any> ) {
			if( params.opacity !== undefined ) widget.opacity(params.opacity)
			if( params.z_order !== undefined ) widget.z(params.z_order)
			if( params.background_color !== undefined ) widget.background_color(params.background_color)
			if( params.border_color !== undefined ) widget.border_color(params.border_color)
			if( params.border_width !== undefined ) widget.border_width(params.border_width)
			if( params.border_style !== undefined ) widget.border_style(params.border_style)
			if( params.border_opacity !== undefined ) widget.border_opacity(params.border_opacity)
			if( params.ratio !== undefined ) widget.viewport_frame().ratio(params.ratio)
			if( params.locked !== undefined ) widget.locked(params.locked)
		}

		widget_update( id: string, params: Record< string, any > ) {
			const widget = this.widget_by_id( id )
			if( !widget ) throw new Error( `Widget "${ id }" not found` )
			if( params.type !== undefined ) this.widget_type_check( String( params.type ) )
			this.widget_params_check( String(params.type ?? widget.type()), params )

			if( params.title !== undefined ) widget.title( String( params.title ) )
			if( params.text !== undefined ) widget.text( String( params.text ) )
			if( params.type !== undefined ) widget.type( String( params.type ) as any )
			if( params.data !== undefined ) widget.data( params.data )
			if( params.enabled !== undefined ) widget.enabled( Boolean( params.enabled ) )
			this.widget_move_resize_optional( widget, params )
			this.widget_style_apply( widget, params )

			this.register()
			return this.widget_info_from( widget, true )
		}

		widget_move_resize( id: string, params: Record< string, any > ) {
			const widget = this.widget_by_id( id )
			if( !widget ) throw new Error( `Widget "${ id }" not found` )
			this.widget_params_check( widget.type(), params )
			this.widget_move_resize_optional( widget, params )
			this.register()
			return this.widget_info_from( widget, true )
		}

		widget_move_resize_optional( widget: $shm_hitalama_map_widget, params: Record< string, any > ) {
			if( params.x !== undefined ) widget.body_x( Number( params.x ) )
			if( params.y !== undefined ) widget.body_y( Number( params.y ) )
			if( params.width !== undefined ) widget.right_edge_x( Number( params.width ) )
			if( params.height !== undefined ) widget.bottom_edge_y( Number( params.height ) )
		}

		widget_open_view( id: string, params: Record< string, any > ) {
			const widget = this.widget_by_id( id )
			if( !widget ) throw new Error( `Widget "${ id }" not found` )
			this.widget_params_check( widget.type(), params )

			const view = widget.code_view_widget_open()
			if( !view ) throw new Error( `Widget "${ widget.title() || id }" does not support a view widget` )
			if( params.title !== undefined ) view.title( String( params.title ) )
			this.widget_move_resize_optional( view, params )

			this.register()
			return this.widget_info_from( view, true )
		}

		code_source_widget( widget: $shm_hitalama_map_widget ) {
			const code = widget.code_widget()
			if( code ) return code
			if( widget.code_view_widget() ) return widget
			return null
		}

		async widget_result( id: string, params: Record< string, any > ) {
			const widget = this.widget_by_id( id )
			if( !widget ) throw new Error( `Widget "${ id }" not found` )

			const source = this.code_source_widget( widget )
			if( !source ) throw new Error( `Widget "${ widget.title() || id }" does not support code result` )

			let view: $shm_hitalama_map_widget | null | undefined
			if( params.open_view !== false ) view = source.code_view_widget_open()
			else view = source.view_widget()

			const runner = source.map().runtime().widget_runner( source.link_str() )
			const wait_ms = Math.max( 0, Number( params.wait_ms ?? 8000 ) )
			let result: unknown
			let error: unknown
			let timed_out = false

			if( params.run !== false ) {
				const run = this.$.$mol_wire_async( runner ).run_auto( null )
				Promise.resolve( run ).catch( ()=> null )

				try {
					result = wait_ms
						? await this.promise_timeout( run, wait_ms )
						: await run
				} catch( next ) {
					if( next === $shm_hitalama_mcp_client_timeout ) timed_out = true
					else error = next
				}
			}

			const process = this.$.$shm_hitalama_process.last_named( runner, 'run_auto' )
			const data = process?.data()
			if( error === undefined && data?.status === 'failed' ) error = data.error
			if( result === undefined && !timed_out && data?.status === 'done' ) result = data.result
			if( error instanceof Error ) source.map().runtime().runtime_sourcemap().error_stack_remap( error )

			this.register()
			return {
				ok: error === undefined && !timed_out,
				status: timed_out ? 'running' : data?.status ?? ( params.run === false ? 'idle' : 'done' ),
				source: this.widget_info_from( source, false ),
				view: view ? this.widget_info_from( view, false ) : null,
				result: error === undefined ? this.result_json( result ) : undefined,
				error: error === undefined ? undefined : this.error_json( error ),
				started_at: data?.started_at?.toString?.() ?? undefined,
				finished_at: data?.finished_at?.toString?.() ?? undefined,
				duration_ms: process?.duration?.(),
				runs_count: data?.runs_count,
				timed_out,
			}
		}

		promise_timeout< Value >( promise: PromiseLike< Value >, ms: number ) {
			return new Promise< Value >( ( resolve, reject )=> {
				const timer = setTimeout( ()=> reject( $shm_hitalama_mcp_client_timeout ), ms )
				Promise.resolve( promise ).then(
					value => {
						clearTimeout( timer )
						resolve( value )
					},
					error => {
						clearTimeout( timer )
						reject( error )
					},
				)
			} )
		}

		widget_delete( id: string ) {
			const map = this.map()
			if( !map ) throw new Error( 'No Hitalama map is open in this browser client' )
			const widget = this.widget_by_id( id )
			if( !widget ) throw new Error( `Widget "${ id }" not found` )
			map.widget_cut( widget.link() )
			this.register()
			return { ok: true, id: widget.link().str }
		}

		map_store_get( key: string ) {
			if( !key ) throw new Error( 'Store key is required' )
			const map = this.map()
			if( !map ) throw new Error( 'No Hitalama map is open in this browser client' )
			return map.store( key )
		}

		map_store_set( key: string, value: any ) {
			if( !key ) throw new Error( 'Store key is required' )
			const map = this.map()
			if( !map ) throw new Error( 'No Hitalama map is open in this browser client' )
			return map.store( key, value )
		}

		async execute_js( code: string ) {
			const map = this.map()
			if( !map ) throw new Error( 'No Hitalama map is open in this browser client' )
			if( !code ) throw new Error( 'Code is required' )

			const result = await this.$.$mol_wire_async( map ).execute_action( code, { [ '$' + 'page' ]: this.map_page() } )
			this.register()
			return this.result_json( result )
		}

		result_json( value: any ): any {
			if( value instanceof Error ) return { type: 'Error', ...this.error_json( value ) }
			if( typeof value === 'bigint' ) return { type: 'bigint', value: String( value ) }
			if( value == null ) return value
			if( typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ) return value
			if( value instanceof Element ) return {
				type: 'Element',
				tag: value.tagName.toLowerCase(),
				text: value.textContent?.slice( 0, 500 ) ?? '',
			}
			if( value instanceof this.$.$mol_view ) return {
				type: '$mol_view',
				class: value.constructor.name,
			}

			try {
				return JSON.parse( JSON.stringify( value, ( key, item )=> {
					if( typeof item === 'bigint' ) return { type: 'bigint', value: String( item ) }
					if( item instanceof Error ) return { type: 'Error', ...this.error_json( item ) }
					return item
				} ) )
			} catch( error ) {
				return String( value )
			}
		}

		error_json( error: unknown ): any {
			if( this.$.$mol_promise_like( error ) ) return {
				name: 'Promise',
				message: 'Async result is still pending',
			}

			if( error instanceof Error ) return {
				name: error.name,
				message: this.$.$mol_error_message( error ),
				stack: this.error_stack_text( error ),
			}

			return {
				name: typeof error,
				message: String( error ),
			}
		}

		error_stack_text( error: Error ) {
			const remapped = ( error as { $shm_hitalama_map_runtime_stack?: unknown } ).$shm_hitalama_map_runtime_stack
			if( typeof remapped === 'string' ) return remapped

			const stack = ( error as { stack?: unknown } ).stack
			if( typeof stack === 'string' ) return stack
			if( Array.isArray( stack ) ) return stack.map( frame => this.error_frame_text( frame ) ).join( '\n' )

			return undefined
		}

		error_frame_text( frame: any ) {
			const title = frame?.function || frame?.method || '<anonymous>'
			const source = frame?.source || frame?.eval || ''
			const pos = Array.isArray( frame?.pos )
				? `:${ Number( frame.pos[0] ) + 1 }:${ Number( frame.pos[1] ) + 1 }`
				: ''

			return source ? `${ title } @ ${ source }${ pos }` : title
		}

		override destructor() {
			this.skills_service?.destructor()
			this.application_service?.destructor()
			const socket = this.socket
			if( socket ) {
				socket.onopen = null
				socket.onmessage = null
				socket.onclose = null
				socket.onerror = null
				socket.close()
			}
			this.socket = null
			this.socket_url = ''
			super.destructor()
		}

	}

}
